import type { PublicItinerary, PublicItineraryNode, PublicItinerarySegment, PublicTimeSlot } from "../types";
import { publicRouteEndpointTitles } from "../service/itineraryOutputService";

const timeSlotOrder: PublicTimeSlot[] = ["morning", "afternoon", "evening", "unscheduled"];

/** 按客户行程的时段与序号还原当前点位顺序。 */
export function orderPublicItineraryNodes(nodes: PublicItineraryNode[]): PublicItineraryNode[] {
  return [...nodes].sort((left, right) =>
    timeSlotOrder.indexOf(left.timeSlot) - timeSlotOrder.indexOf(right.timeSlot)
    || left.sortOrder - right.sortOrder,
  );
}

/** 使用与公开 DTO 相同的端点展示名匹配相邻交通，兼容同名异类节点的类型后缀。 */
export function publicSegmentMatchesNodes(
  segment: PublicItinerarySegment,
  fromNode: PublicItineraryNode,
  toNode: PublicItineraryNode,
): boolean {
  const [fromTitle, toTitle] = publicRouteEndpointTitles(fromNode, toNode);
  return segment.fromTitle === fromTitle && segment.toTitle === toTitle;
}

/** 只保留当前相邻点序能够匹配的客户交通段。 */
export function canonicalPublicSegments(
  nodes: PublicItineraryNode[],
  segments: PublicItinerarySegment[],
): PublicItinerarySegment[] {
  const ordered = orderPublicItineraryNodes(nodes);
  const unused = new Set(segments);
  return ordered.slice(0, -1).flatMap((node, index) => {
    const next = ordered[index + 1];
    const segment = segments.find((candidate) =>
      unused.has(candidate) && publicSegmentMatchesNodes(candidate, node, next),
    );
    if (!segment) return [];
    unused.delete(segment);
    return [segment];
  });
}

/** 跨日步行若完全没有事实则只是结构占位，不进入客户阅读流。 */
export function customerVisibleInterDaySegments(
  segments: PublicItinerarySegment[],
): PublicItinerarySegment[] {
  return segments.filter((segment) => segment.isInterDay && (
    segment.transportMode !== "walk"
    || segment.isPending
    || Boolean(
      segment.routeCode
      || segment.departureAt
      || segment.arrivalAt
      || segment.distanceKm
      || segment.durationMinutes
      || segment.description
      || segment.primaryTransport
      || segment.backupTransports.length,
    )
  ));
}

/** 识别跨夜交通完整经过的中间日期，仅依据已有发到时间，不补造缺失行程。 */
export function customerTransitSegmentsForDate(
  days: PublicItinerary["days"],
  dayDate?: string | null,
): PublicItinerarySegment[] {
  const targetDate = isoDatePart(dayDate);
  if (!targetDate) return [];
  return days.flatMap((day) => customerVisibleInterDaySegments(day.segments)).filter((segment) => {
    const departureDate = isoDatePart(segment.departureAt);
    const arrivalDate = isoDatePart(segment.arrivalAt);
    return Boolean(departureDate && arrivalDate && departureDate < targetDate && targetDate < arrivalDate);
  });
}

/** 从本地日期或 ISO 时间中提取可比较的日历日期。 */
function isoDatePart(value?: string | null): string | null {
  return value?.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? null;
}
